import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import api from "../../lib/api";
import { toast } from "sonner";
import { ArrowRight, Calendar, AlertTriangle, ClipboardList } from "lucide-react";

const STAGE_LABELS = {
  awaiting_acceptance: "Awaiting Acceptance",
  active_care: "Active Care",
  completed: "Completed",
  unassigned: "Unassigned",
};

function stageBadge(s) {
  const styles = {
    awaiting_acceptance: "bg-amber-50 text-amber-800 border-amber-200",
    active_care: "bg-emerald-50 text-emerald-800 border-emerald-200",
    completed: "bg-slate-50 text-slate-500 border-slate-200",
    unassigned: "bg-slate-100 text-slate-700 border-slate-200",
  }[s] || "bg-slate-50 text-slate-700 border-slate-200";
  return (
    <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border ${styles}`}>
      {STAGE_LABELS[s] || s}
    </span>
  );
}

export default function ChaplainDashboard({ filter }) {
  // filter: undefined = new+active, "follow_up" = follow_up_due bucket, "completed" = completed only
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/admin/care-coordination/my-assignments/list");
      setRecords(r.data || []);
    } catch (e) {
      toast.error("Failed to load assignments");
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { load(); }, []);

  const today = new Date().toISOString().slice(0, 10);
  const filtered = records.filter((r) => {
    if (filter === "follow_up") return r.care_stage === "active_care" && r.follow_up_date && r.follow_up_date <= today;
    if (filter === "completed") return r.care_stage === "completed";
    return r.care_stage === "awaiting_acceptance" || r.care_stage === "active_care";
  });

  const title = filter === "follow_up" ? "Follow-Up Due" : filter === "completed" ? "Completed Care" : "My Assignments";
  const testKey = filter || "assignments";

  return (
    <div data-testid={`chap-dashboard-${testKey}`}>
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div>
          <h2 className="font-heading text-2xl tracking-tight text-slate-900 flex items-center gap-2">
            <ClipboardList className="w-6 h-6 text-[#4A6FA5]" strokeWidth={1.75} /> {title}
          </h2>
          <p className="mt-1 text-sm text-slate-500 max-w-2xl">
            {filter === "completed"
              ? "Records you've completed. History is preserved and remains visible to authorized supervisors."
              : filter === "follow_up"
              ? "Assignments that have a follow-up date today or earlier and are not yet completed."
              : "Assignments waiting on your acceptance and active care you're currently managing."}
          </p>
        </div>
      </div>

      <div className="mt-6 bg-white border border-slate-200 rounded-2xl overflow-hidden" data-testid="chap-records-list">
        {loading ? (
          <p className="p-6 text-sm text-slate-500">Loading…</p>
        ) : filtered.length === 0 ? (
          <p className="p-6 text-sm text-slate-500">Nothing in this view.</p>
        ) : (
          <ul className="divide-y divide-slate-100">
            {filtered.map((r) => (
              <li key={r.id} className="p-4 flex items-start justify-between gap-4 hover:bg-slate-50">
                <div className="min-w-0">
                  <div className="flex flex-wrap items-center gap-2">
                    {stageBadge(r.care_stage)}
                    {r.priority === "urgent_admin_review" && (
                      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded text-xs font-medium border bg-red-50 text-red-800 border-red-200">
                        <AlertTriangle className="w-3 h-3" /> Urgent Admin Review
                      </span>
                    )}
                    {r.priority === "time_sensitive" && (
                      <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium border bg-orange-50 text-orange-800 border-orange-200">
                        Time-Sensitive
                      </span>
                    )}
                  </div>
                  <p className="mt-1 text-sm font-medium text-slate-900">
                    {r.first_name} {r.last_name}
                    <span className="text-slate-400 font-normal"> · {r.company_name}</span>
                  </p>
                  <p className="text-xs text-slate-500 mt-1">
                    <Calendar className="inline w-3.5 h-3.5 mr-1" />
                    Received {new Date(r.created_at).toLocaleDateString()}
                    {r.follow_up_date ? ` · Follow-up ${r.follow_up_date}` : ""}
                    {r.next_action ? ` · Next: ${r.next_action}` : ""}
                  </p>
                </div>
                <Link
                  to={`/chaplain-portal/care/${r.id}`}
                  data-testid={`chap-open-${r.id}`}
                  className="inline-flex items-center gap-1 px-3 py-1.5 rounded-lg text-xs font-medium bg-[#4A6FA5] text-white hover:bg-[#3B597B] shrink-0"
                >
                  Open <ArrowRight className="w-3 h-3" />
                </Link>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}
